import React, { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { FormProps } from '~types'
import { fireAnalytics, formatAnalyticsText, getPageType } from '~analyticsUtils'
import { ANALYTICS_EVENT_BUTTON_CLICK, ANALYTICS_LINK_LOCATION_FORM, ANALYTICS_LINK_MODULE_GLOBAL_NAV } from '~analyticsConstants'
import { Heading, Modal } from '~elements'
import styles from './Form.module.scss'
import { useWindowSize } from 'utils/hooks'
import Image from 'next/image'

const ReactFormstack = dynamic(() => import('@msgtechnology/react-formstack').then(module => module.default), { ssr: false })

const useMutationObserver = (
  ref: any,
  callback: any,
  options = {
    attributes: false,
    characterData: false,
    childList: true,
    subtree: true
  }
) => {
  React.useEffect(() => {
    if (ref.current) {
      const observer = new MutationObserver(callback)
      observer.observe(ref.current, options)
      return () => observer.disconnect()
    }
  }, [callback, options])
}

interface FormstackData {
  id: string
}

const Form = ({
  autoComplete = true,
  formId,
  formSubmitHandler,
  inputBgColor = 'white',
  isChildOfModal = false,
  submitButtonBgColor = 'white',
  submitButtonText = 'Submit',
  subtitle,
  title
}: FormProps) => {
  const mutationRef = React.useRef<HTMLDivElement>(null)
  const [mutationCount, setMutationCount] = React.useState(0)
  const incrementMutationCount = () => {
    return setMutationCount(mutationCount + 1)
  }

  // Use to identify delayed children update in form
  useMutationObserver(mutationRef, incrementMutationCount)

  const { asPath } = useRouter()
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false)
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)

  const modalIsOpenHandler = (state: boolean) => {
    setModalIsOpen(state)
  }

  const analyticsEvent = () => {
    fireAnalytics(ANALYTICS_EVENT_BUTTON_CLICK, getPageType(asPath), {
      linkId: formatAnalyticsText(submitButtonText),
      linkModule: ANALYTICS_LINK_MODULE_GLOBAL_NAV,
      linkLocation: ANALYTICS_LINK_LOCATION_FORM
    })
  }

  const afterSubmit = (data: FormstackData) => {
    if (data.id) {
      setIsSuccessful(true)
      modalIsOpenHandler(true)
      analyticsEvent()
    }
  }

  useEffect(() => {
    if (mutationCount > 0 && mutationRef.current) {
      const dropDownFieldSet = document.getElementById('fs-fieldset__area_of_interest')

      if (dropDownFieldSet) {
        // Remove unnecessary Required message
        const errorDivElements = dropDownFieldSet.querySelectorAll('.fs-error-message')
        errorDivElements?.forEach(el => el.setAttribute('style', 'display:none;visibility:collapse;'))

        // Customize select open indicator as per figma
        const customControlDiv = document.querySelector('.fs-select-custom__control, .fs-error-input__control')
        if (customControlDiv) {
          customControlDiv.setAttribute('id', 'fs-select-custom')
        }

        // Customize select placeholder to have defined value
        const reactSelect3Placeholder = document.querySelector<HTMLDivElement>('.fs-error-input__placeholder')
        if (reactSelect3Placeholder && reactSelect3Placeholder.textContent === 'Select...' && mutationCount < 10) {
          reactSelect3Placeholder.setAttribute('style', 'color: var(--gray-300);font-size:19px;font-weight:350;')
          reactSelect3Placeholder.innerText = 'Area of Interest *'
        }

        // Single selected should be of white as per other controls
        const customControlDivSingleValue = document.querySelector('.fs-select-custom__single-value')
        if (customControlDivSingleValue) {
          customControlDivSingleValue.setAttribute('id', 'fs-select-custom-single-value')
        }

        // Customize select open indicator as per figma
        const fsSelectCustomIndicators = document.querySelector('.fs-select-custom__indicators, .fs-error-input__indicators')
        if (fsSelectCustomIndicators) {
          fsSelectCustomIndicators.setAttribute('id', 'fs-select-custom-indicators')
        }

        // handle select menu background and selected option
        const customControlMenu = document.querySelector('.fs-select-custom__menu, .fs-error-input__menu')

        if (customControlMenu) {
          customControlMenu.setAttribute('id', 'fs-select-custom-menu')
          const selectedElement = customControlMenu.querySelector('.fs-select-custom__option--is-selected')

          if (selectedElement) {
            selectedElement.setAttribute('style', 'background:black;color: gray;')
          }
        }
      }
    }
  }, [mutationCount])

  const formClassNames = `formstack formstack--input-bg-color-transparent formstack--submit-btn-bg-${submitButtonBgColor}`

  const getBgUrl = () => {
    if (isBelowBreakpointMd) {
      return `url("assets/partnership_landing_form section_768.png")`
    } else {
      return `url("assets/partnership_landing_form section_1920.png")`
    }
  }

  const formstackApi = process.env.NEXT_PUBLIC_FORMSTACK_API_BASE

  if (!formstackApi || !formId) return null

  return (
    <div id="contact-form" className={styles['main-container']} style={{ backgroundImage: getBgUrl() }}>
      <div className={styles['main-container-overlay']}></div>
      <div className={styles['form-container']}>
        <div className={styles['form-actual-container']}>
          <div className={styles['form-header-container']}>
            <Heading level={1}>{title}</Heading>
            {subtitle && (
              <Heading level={6} letterSpacing="0.8px" fontWeight="350" margin="0">
                {subtitle}
              </Heading>
            )}
          </div>
          <div className={styles['form-body']}>
            <small>Fields with an * are required.</small>
            <div ref={mutationRef}>
              <ReactFormstack
                endpoint={formstackApi}
                autoComplete={autoComplete}
                className={formClassNames}
                id={formId}
                onAfterSubmit={afterSubmit}
                shouldResetOnSubmit={true}
                submitButtonText={submitButtonText}
                fieldEnhancements={{
                  select: { useSelect2Ui: true },
                  phone: {
                    validationUi: false,
                    validationMessage: 'Please enter a valid 10-digit, U.S. phone number.'
                  },
                  email: {
                    validationUi: false,
                    validationMessage: 'Please enter a valid email address.'
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={modalIsOpen} handleModalClose={() => modalIsOpenHandler(false)}>
        <div className={styles['form-modal-wrapper']}>
          <div className={styles['form-success']}>
            <div className={'image-container'}>
              <Image
                className={styles['form-success__icon']}
                src="/assets/icon-check-mark.png"
                alt="Form Success"
                width="0"
                height="0"
                sizes="100vw"
                priority
              />
            </div>
          </div>

          <Heading level={5}>Thank you! Your inquiry has been sent!</Heading>
        </div>
      </Modal>
    </div>
  )
}

export default Form
